import { useEffect, useRef, useState } from "react";
import { Send, Mic, Square, Paperclip, Link2, X, Loader2, Smile, ImageIcon, FileText, Camera, Pencil, Reply } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useRecorder } from "../Chat";
import { FormDialog } from "../FormDialog";
import { PersonAvatar } from "../UserImage";

const PICK = ["😀", "😁", "😂", "🤣", "😊", "😍", "😘", "😎", "🤔", "😅", "😇", "🙂", "😉", "😴", "😢", "😭", "😡", "😱", "🥳", "🤝", "👍", "👎", "👏", "🙌", "🙏", "💪", "👌", "✌️", "❤️", "🔥", "✨", "🎉", "✅", "❌", "⚠️", "📌", "📎", "📷", "🎨", "🎬", "🏁", "⏰", "💯", "👀", "🚀", "☕"];
const KINDS = [["message", "Message"], ["update", "Update"], ["decision", "Decision"], ["escalation", "Escalation"]];

function Emoji({ onPick }) {
  return (
    <Popover>
      <PopoverTrigger data-testid="chat-emoji-button" title="Emoji" className="rounded-full p-2 text-zinc-500 transition-colors hover:bg-black/5"><Smile className="h-6 w-6" /></PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-72 p-2">
        <div className="grid grid-cols-8 gap-1">{PICK.map((e, i) => <button key={e} data-testid={`chat-emoji-${i}`} onClick={() => onPick(e)} className="rounded p-1 text-xl transition-transform hover:scale-125 hover:bg-zinc-100">{e}</button>)}</div>
      </PopoverContent>
    </Popover>
  );
}

function AttachMenu({ busy, onFile, onLink }) {
  const media = useRef(null), doc = useRef(null), cam = useRef(null);
  const pick = (e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onFile(f); };
  const opt = (id, Icon, label, tone, fn) => (
    <button data-testid={`chat-attach-${id}`} onClick={fn} className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm text-zinc-700 hover:bg-zinc-100">
      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-white ${tone}`}><Icon className="h-4 w-4" /></span>{label}
    </button>
  );
  return (
    <Popover>
      <PopoverTrigger data-testid="chat-attach-button" disabled={busy} title="Attach" className="rounded-full p-2 text-zinc-500 transition-colors hover:bg-black/5">{busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Paperclip className="h-6 w-6" />}</PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-56 p-1.5">
        {opt("media", ImageIcon, "Photos & videos", "bg-sky-500", () => media.current?.click())}
        {opt("camera", Camera, "Camera", "bg-rose-500", () => cam.current?.click())}
        {opt("document", FileText, "Document / PDF", "bg-violet-500", () => doc.current?.click())}
        {opt("link", Link2, "Link", "bg-[#A9792C]", onLink)}
      </PopoverContent>
      <input ref={media} data-testid="chat-media-input" type="file" accept="image/*,video/*" className="hidden" onChange={pick} />
      <input ref={cam} data-testid="chat-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
      <input ref={doc} data-testid="chat-file-input" type="file" accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.csv,.txt" className="hidden" onChange={pick} />
    </Popover>
  );
}

function MentionList({ items, active, onPick }) {
  if (!items.length) return null;
  return (
    <div data-testid="chat-mention-list" className="thin-scroll absolute bottom-full left-0 z-30 mb-2 max-h-60 w-72 overflow-y-auto rounded-xl border border-zinc-200 bg-white p-1 shadow-xl">
      {items.map((u, i) => (
        <button key={u.id} type="button" data-testid={`chat-mention-option-${u.name.replace(/\W+/g, "-").toLowerCase()}`} onMouseDown={(e) => { e.preventDefault(); onPick(u); }}
          className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm ${i === active ? "bg-[#15966B]/10 text-[#102A25]" : "text-zinc-700 hover:bg-zinc-50"}`}>
          <PersonAvatar name={u.name} size={26} /><span className="truncate font-medium">{u.name}</span>
        </button>
      ))}
    </div>
  );
}

export function Composer({ channel, label, members = [], isChannel, replyTo, editing, onCancel, onSend, onSaveEdit, onTyping }) {
  const [mention, setMention] = useState(null);
  const [picked, setPicked] = useState([]);
  const [active, setActive] = useState(0);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("message");
  const [busy, setBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const ta = useRef(null);
  useEffect(() => { setText(editing ? editing.text : ""); setMention(null); setPicked([]); ta.current?.focus(); }, [editing, channel]);
  useEffect(() => { if (replyTo) ta.current?.focus(); }, [replyTo]);
  useEffect(() => { if (ta.current) { ta.current.style.height = "auto"; ta.current.style.height = `${Math.min(ta.current.scrollHeight, 140)}px`; } }, [text]);

  const upload = async (blob, name, caption = "") => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, name);
      fd.append("entity_type", "message");
      fd.append("entity_id", channel);
      fd.append("category", "chat");
      const { data } = await api.post("/files", fd);
      await onSend({ text: caption, kind, attachment: { id: data.id, name: data.original_filename, content_type: data.content_type, size: data.size } });
      setText("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const { recording, secs, start, stop } = useRecorder((b) => upload(b, `voice-note-${Date.now()}.webm`, ""));
  const matches = mention ? members.filter((u) => u.name.toLowerCase().includes(mention.q.toLowerCase())).slice(0, 30) : [];
  const detect = (value, caret) => {
    const m = value.slice(0, caret).match(/(^|\s)@([^\s@]*)$/);
    setMention(m ? { start: caret - m[2].length - 1, end: caret, q: m[2] } : null);
    setActive(0);
  };
  const pick = (u) => {
    const next = `${text.slice(0, mention.start)}@${u.name} ${text.slice(mention.end)}`;
    setText(next);
    setPicked((p) => (p.some((x) => x.id === u.id) ? p : [...p, u]));
    setMention(null);
    const pos = mention.start + u.name.length + 2;
    requestAnimationFrame(() => { ta.current?.focus(); ta.current?.setSelectionRange(pos, pos); });
  };
  const mentionsIn = (t) => picked.filter((u) => t.includes(`@${u.name}`)).map((u) => u.id);
  const submit = async () => {
    if (!text.trim()) return;
    const t = text;
    setText("");
    setMention(null);
    if (editing) await onSaveEdit(t); else await onSend({ text: t, kind, mentions: mentionsIn(t), channel_label: label });
    setPicked([]);
    setKind("message");
  };

  return (
    <div className="bg-[#F6F1E6] px-2 py-2 sm:px-4">
      {(replyTo || editing) && (
        <div data-testid="chat-composer-context" className="mb-2 flex items-start gap-2 rounded-lg border-l-4 border-[#A9792C] bg-white px-3 py-2">
          {editing ? <Pencil className="mt-0.5 h-4 w-4 text-[#A9792C]" /> : <Reply className="mt-0.5 h-4 w-4 text-[#A9792C]" />}
          <div className="min-w-0 flex-1"><div className="text-xs font-semibold text-[#A9792C]">{editing ? "Edit message" : `Replying to ${replyTo.author}`}</div><div className="truncate text-xs text-zinc-500">{(editing || replyTo).text || "Attachment"}</div></div>
          <button data-testid="chat-composer-cancel" onClick={() => { onCancel(); setText(""); }} className="text-zinc-400 hover:text-zinc-700"><X className="h-4 w-4" /></button>
        </div>
      )}
      {recording ? (
        <div className="flex items-center gap-3 rounded-full bg-white px-4 py-2" data-testid="chat-recording">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500" />
          <span className="font-mono text-sm text-rose-600">{String(Math.floor(secs / 60)).padStart(2, "0")}:{String(secs % 60).padStart(2, "0")}</span>
          <span className="text-xs text-zinc-400">Recording voice note…</span>
          <div className="ml-auto flex gap-1">
            <button data-testid="chat-record-cancel" onClick={() => stop(true)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-100"><X className="h-5 w-5" /></button>
            <button data-testid="chat-record-stop" onClick={() => stop(false)} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#A9792C] text-white"><Square className="h-4 w-4" /></button>
          </div>
        </div>
      ) : (
        <div className="flex items-end gap-1">
          <Emoji onPick={(e) => setText((t) => t + e)} />
          {!editing && <AttachMenu busy={busy} onFile={(f) => upload(f, f.name, text.trim())} onLink={() => setLinkOpen(true)} />}
          <div className="relative flex min-w-0 flex-1 items-end rounded-2xl bg-white px-3 py-1.5">
            {!editing && <MentionList items={matches} active={active} onPick={pick} />}
            <textarea ref={ta} rows={1} data-testid="chat-message-input" value={text} placeholder="Type a message — type @ to tag someone"
              onChange={(e) => { setText(e.target.value); detect(e.target.value, e.target.selectionStart); onTyping(); }}
              onKeyDown={(e) => {
                if (matches.length && !editing) {
                  if (e.key === "ArrowDown") { e.preventDefault(); return setActive((a) => (a + 1) % matches.length); }
                  if (e.key === "ArrowUp") { e.preventDefault(); return setActive((a) => (a - 1 + matches.length) % matches.length); }
                  if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); return pick(matches[active]); }
                  if (e.key === "Escape") { e.preventDefault(); return setMention(null); }
                }
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } if (e.key === "Escape") onCancel();
              }}
              onBlur={() => setTimeout(() => setMention(null), 150)}
              className="thin-scroll max-h-[140px] flex-1 resize-none bg-transparent py-1 text-[15px] outline-none" />
            {isChannel && !editing && (
              <select data-testid="chat-kind-select" value={kind} onChange={(e) => setKind(e.target.value)} title="Tag this message" className="ml-1 rounded-md bg-zinc-100 px-1.5 py-1 text-[11px] text-zinc-600 outline-none">
                {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
          </div>
          {text.trim() || editing ? (
            <button data-testid="chat-send-button" onClick={submit} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#A9792C] text-white transition-transform hover:scale-105"><Send className="h-5 w-5" /></button>
          ) : (
            <button data-testid="chat-mic-button" title="Record voice note" disabled={busy} onClick={start} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#A9792C] text-white transition-transform hover:scale-105"><Mic className="h-5 w-5" /></button>
          )}
        </div>
      )}
      <FormDialog testid="chat-link-form" open={linkOpen} onOpenChange={setLinkOpen} title="Send a link" submitLabel="Send link" initial={{}}
        fields={[{ name: "url", label: "Link URL (https://…)", required: true, full: true }, { name: "label", label: "Description (optional)", full: true }]}
        onSubmit={async (f) => {
          const url = /^https?:\/\//.test(f.url) ? f.url : `https://${f.url}`;
          return !!(await onSend({ text: f.label ? `${f.label}\n${url}` : url, link: url, kind }));
        }} />
    </div>
  );
}
